<template>
  <UIMenu menu-width="auto">
    <template #activator="{ props: activatorProps }">
      <v-btn
        v-bind="activatorProps"
        :disabled="isLoading"
        :loading="isLoading"
        color="secondary"
        class="w-full capitalize text-primary"
        variant="flat"
      >
        <v-icon class="mr-2" size="small">mdi-translate</v-icon>
        {{ currentLanguageLabel }}
        <v-icon class="ml-2" size="small">mdi-chevron-down</v-icon>
      </v-btn>
    </template>
    <v-list class="language-list w-full">
      <v-list-item
        v-for="item in languagesForDisplay"
        :key="item.code"
        :active="isActive(item.code)"
        :class="{
          'text-primary': isActive(item.code),
        }"
        link
        @click="handleLanguageChange(item.code)"
      >
        <v-list-item-title>
          {{ item.label }}
        </v-list-item-title>
        <v-list-item-subtitle class="text-xs">
          {{ item.currentLanguageLabel }}
        </v-list-item-subtitle>
      </v-list-item>
    </v-list>
  </UIMenu>
</template>

<script lang="ts" setup>
import { useLanguages } from '@manager/composables/use-languages';
import { useUserSettingsStore } from '@manager/store/user-settings-store';
import UIMenu from 'shared/ui/registry/kinnovis/ui-menu/UIMenu.vue';
import {
  getLocalizedLanguageLabel,
  getNativeLanguageLabel,
} from 'shared/utils';
import { computed, ref } from 'vue';

const { userInterfaceLanguages } = useLanguages();
const userSettingsStore = useUserSettingsStore();
const currentLanguage = computed(() => userSettingsStore.locale);

const isLoading = ref(false);

type ILanguageOption = {
  code: string;
  label: string;
  currentLanguageLabel: string;
};

function isActive(code: string) {
  return code.toLowerCase() === currentLanguage.value.toLowerCase();
}

const languagesForDisplay = computed<ILanguageOption[]>(() => {
  return userInterfaceLanguages.value
    .map((code) => {
      return {
        code,
        label: getNativeLanguageLabel(code),
        currentLanguageLabel: getLocalizedLanguageLabel(
          code,
          currentLanguage.value
        ),
      };
    })
    .filter((item) => !!item.code)
    .sort((a, b) => a.label.localeCompare(b.label, currentLanguage.value));
});

const currentLanguageLabel = computed(() => {
  const label = getNativeLanguageLabel(currentLanguage.value);

  return label || currentLanguage.value;
});

function handleLanguageChange(newLocale: string) {
  isLoading.value = true;
  userSettingsStore.changeLanguage(newLocale);
}
</script>

<style scoped lang="scss">
.language-list {
  max-height: 288px;
  overflow-y: auto;
}
</style>
